import { useState, useEffect, useContext } from "react";
import {
  FaBox,
  FaWarehouse,
  FaExclamationTriangle,
  FaClock,
  FaChartLine,
  FaPlus,
  FaWifi,
  FaTimes,
  FaSync,
} from "react-icons/fa";
import { motion } from "framer-motion";
import AuthContext from "../../contexts/AuthContext";
import {
  getInventoryItems,
  getStockMovements,
  getOpeningStock,
  getMainStoreData,
} from "../../utils/inventoryApi";
import { inventoryListeners } from "../../utils/firebase";

export default function InventoryDashboard() {
  const { restaurantId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState("connected");

  // Set real-time status to connected by default
  useEffect(() => {
    setRealtimeStatus("connected");
  }, []);
  const [stats, setStats] = useState({
    mainStoreItems: 0,
    kitchenItems: 0,
    lowStockAlerts: 0,
    overStockItems: 0,
    totalValue: 0,
    recentMovements: [],
  });
  const [error, setError] = useState("");

  useEffect(() => {
    fetchInventoryStats();
    const cleanup = setupRealtimeListeners();

    // Cleanup function
    return () => {
      if (cleanup) cleanup();
    };
  }, [restaurantId]);

  function setupRealtimeListeners() {
    if (!restaurantId) return;

    // Manual refresh only - no automatic polling
    // Users can refresh manually using the refresh button
    setRealtimeStatus("connected");

    // Cleanup function
    return () => {
      // No cleanup needed for manual refresh
    };
  }

  // These functions are kept for future real-time implementation
  function updateStatsFromItems(items) {
    const mainStoreItems = items.filter(
      (i) => i.location === "Main Store"
    ).length;
    const kitchenItems = items.filter((i) => i.location === "Kitchen").length;
    const totalValue = items.reduce(
      (sum, i) => sum + i.currentStock * (i.costPrice || 0),
      0
    );

    setStats((prev) => ({
      ...prev,
      mainStoreItems,
      kitchenItems,
      totalValue,
    }));
  }

  function updateStatsFromMovements(movements) {
    const recentMovements = movements.slice(0, 5);
    setStats((prev) => ({
      ...prev,
      recentMovements,
    }));
  }

  function updateStatsFromAlerts(alerts) {
    const lowStockAlerts = alerts.length;
    setStats((prev) => ({
      ...prev,
      lowStockAlerts,
    }));
  }

  async function fetchInventoryStats() {
    setLoading(true);
    setError("");
    try {
      const [items, movements, openingStock, mainStoreData] = await Promise.all(
        [
          getInventoryItems(restaurantId),
          getStockMovements(restaurantId),
          getOpeningStock(restaurantId, "main"),
          getMainStoreData(restaurantId),
        ]
      );

      // Calculate main store items (opening stock + inventory items)
      const mainStoreItems = (openingStock?.length || 0) + (items?.length || 0);

      // Calculate kitchen items from movements to kitchen
      const kitchenMovements = movements.filter(
        (m) => m.toLocation === "KITCHEN"
      );
      const kitchenItems = kitchenMovements.length;

      // Calculate low stock and over stock items based on Main Store data
      // Using the same logic as MainStore.js getStockStatus function
      const lowStockAlerts = mainStoreData.filter(
        (item) => (item.totalQty || 0) < 10
      ).length;

      const overStockItems = mainStoreData.filter(
        (item) => (item.totalQty || 0) > 100
      ).length;

      // Calculate total value from main store data
      const totalValue = mainStoreData.reduce(
        (sum, item) => sum + (item.totalValue || 0),
        0
      );

      const recentMovements = movements.slice(0, 10);
      setStats({
        mainStoreItems,
        kitchenItems,
        lowStockAlerts,
        overStockItems,
        totalValue,
        recentMovements,
      });
    } catch (err) {
      setError("Failed to fetch inventory stats");
    }
    setLoading(false);
  }

  // Date formatting function
  function formatDate(date) {
    if (!date) return "N/A";

    try {
      let dateObj = null;

      // Handle Firestore Timestamp object with toDate method
      if (date && typeof date.toDate === "function") {
        dateObj = date.toDate();
      }
      // Handle Firestore Timestamp serialized as object with _seconds/_nanoseconds
      else if (
        date &&
        typeof date === "object" &&
        date._seconds !== undefined
      ) {
        dateObj = new Date(date._seconds * 1000);
      }
      // Handle ISO date string
      else if (typeof date === "string") {
        dateObj = new Date(date);
      }
      // Handle regular date object or timestamp
      else {
        dateObj = new Date(date);
      }

      // Validate the date object
      if (!dateObj || isNaN(dateObj.getTime())) {
        return "N/A";
      }

      return dateObj.toLocaleDateString();
    } catch (error) {
      console.error("Date formatting error:", error);
      return "N/A";
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
            Inventory Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1 sm:mt-2">
            Manage your restaurant's stock and supplies
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={fetchInventoryStats}
            className="bg-blue-500 hover:bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base"
          >
            <FaSync className="text-sm" />
            <span className="hidden sm:inline">Refresh Data</span>
            <span className="sm:hidden">Refresh</span>
          </button>
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
              realtimeStatus === "connected"
                ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
            }`}
          >
            {realtimeStatus === "connected" ? <FaWifi /> : <FaTimes />}
            <span className="hidden sm:inline">
              {realtimeStatus === "connected" ? "Live" : "Offline"}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Main Store
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                {stats.mainStoreItems}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                items
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
              <FaBox className="text-blue-600 dark:text-blue-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Kitchen Store
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                {stats.kitchenItems}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                items
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900 rounded-full">
              <FaWarehouse className="text-green-600 dark:text-green-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Low Stock Alerts
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.lowStockAlerts}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                items
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900 rounded-full">
              <FaExclamationTriangle className="text-red-600 dark:text-red-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Over Stock Items
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-600 dark:text-orange-400">
                {stats.overStockItems}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                items
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-orange-100 dark:bg-orange-900 rounded-full">
              <FaExclamationTriangle className="text-orange-600 dark:text-orange-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Inventory Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
      >
        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4">
          Inventory Summary
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 sm:p-4 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400">
                  Total Value
                </p>
                <p className="text-sm sm:text-lg lg:text-xl font-bold text-blue-800 dark:text-blue-200">
                  ₹{stats.totalValue.toLocaleString()}
                </p>
              </div>
              <FaChartLine className="text-blue-500 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 p-3 sm:p-4 rounded-lg border border-green-200 dark:border-green-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400">
                  Recent Movements
                </p>
                <p className="text-sm sm:text-lg lg:text-xl font-bold text-green-800 dark:text-green-200">
                  {stats.recentMovements.length}
                </p>
              </div>
              <FaBox className="text-green-500 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 p-3 sm:p-4 rounded-lg border border-purple-200 dark:border-purple-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400">
                  Stock Alerts
                </p>
                <p className="text-sm sm:text-lg lg:text-xl font-bold text-purple-800 dark:text-purple-200">
                  {stats.lowStockAlerts + stats.overStockItems}
                </p>
              </div>
              <FaExclamationTriangle className="text-purple-500 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>

          
        </div>
      </motion.div>

      {/* Recent Movements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
        className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-2">
          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 dark:text-white">
            Recent Stock Movements
          </h2>
          <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Showing {Math.min(stats.recentMovements.length, 10)} of{" "}
            {stats.recentMovements.length} movements
          </span>
        </div>

        {stats.recentMovements.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <div className="text-gray-400 text-3xl sm:text-4xl mb-2">📦</div>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
              No recent movements found
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-700">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Item Name
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    To
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                {stats.recentMovements.slice(0, 10).map((movement, index) => (
                  <tr
                    key={movement.id}
                    className={`hover:bg-gray-50 dark:hover:bg-slate-700 ${
                      movement.toLocation === "KITCHEN"
                        ? "bg-green-50 dark:bg-green-900/10"
                        : movement.toLocation === "WASTE"
                        ? "bg-red-50 dark:bg-red-900/10"
                        : ""
                    }`}
                  >
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      {index + 1}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      {formatDate(movement.createdAt)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                      {movement.itemName || "N/A"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      <span className="inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {movement.type || "TRANSFER"}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      {movement.quantity || "N/A"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      {movement.fromLocation || "N/A"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      <span
                        className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          movement.toLocation === "KITCHEN"
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : movement.toLocation === "WASTE"
                            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                        }`}
                      >
                        {movement.toLocation || "N/A"}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      {movement.reason || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}
